import { Zodios, makeApi } from "@zodios/core";
import axios from "axios";
import { getURLPrefix } from "../utils/getUrlPrefix";
import { gvadsApiDefinitions } from "./apiDefinitions/gvads";
import { amcApiDefinitions } from "./apiDefinitions/amc";
import { recommendApiDefinitions } from "./apiDefinitions/merchandise";
import { catalogApiDefinitions } from "./apiDefinitions/catalog";
import { experimentalApiDefinitions, pricingApiDefinitions } from "./apiDefinitions/pricing";
import { uploadsApiDefinitions } from "./apiDefinitions/uploads";
import { spApiCredentialCheckDefinitions } from "./apiDefinitions/api/spApiCredentialCheck";
import { workflowsApiDefinitions } from "./apiDefinitions/workflows";
import { shopifyApiDefinitions } from "./apiDefinitions/shopify";
import { appsApiDefinitions } from "./apiDefinitions/api/apps";

import { adReportApiDefinitions } from "./apiDefinitions/api/v4Query/ads";
import { orgReportApiDefinitions } from "./apiDefinitions/api/v4Query/orgReport";
import { productReportApiDefinitions } from "./apiDefinitions/api/v4Query/productReport";
import { pricingPlanReportApiDefinitions } from "./apiDefinitions/api/v4Query/pricingPlanReport";
import { targetReportApiDefinitions } from "./apiDefinitions/api/v4Query/targetReport";
import { productAdsApiDefinitions } from "./apiDefinitions/api/products";
import { auditLogApiDefinitions } from "./apiDefinitions/api/auditLogs";
import { bugReportApiDefinitions } from "./apiDefinitions/api/bugReport";

import { ticketsApiDefinitions, exportCsvEndpoint } from "./apiDefinitions/api/tickets";
import { harvestersApiDefinitions } from "./apiDefinitions/api/harvesters";
import { taggersApiDefinitions } from "./apiDefinitions/api/taggers";
import { programsApiDefinitions } from "./apiDefinitions/api/programs";
import { portfoliosApiDefinitions } from "./apiDefinitions/api/portfolios";
import { scheduleSyncActionsApiDefinitions } from "./apiDefinitions/api/scheduleSyncActions";
import { queryApiDefinitions, stageApiDefinitions, queryStages } from "./apiDefinitions/api/query_funnel";
import { orgBulkActionApiDefinitions } from "./apiDefinitions/api/orgs";
import { profilesApiDefinitions } from "./apiDefinitions/api/profiles";
import { registrationApiDefinitions } from "./apiDefinitions/api/registration";
import { goalsApiDefinitions } from "./apiDefinitions/goals";
import { accountsApiDefinitions } from "./apiDefinitions/api/accounts";
import { organizationsApiDefinitions } from "./apiDefinitions/api/organizations";
import { userProfileApiDefinitions } from "./apiDefinitions/api/userProfile";
import { productAdApiDefinitions } from "./apiDefinitions/api/v4Query/productAds";
import { storePageProductsApiDefinitions } from "./apiDefinitions/api/storePageProducts";
import { searchQueryPerformanceReportApiDefinitions } from "./apiDefinitions/api/v4Query/searchQueryPerformance";
import { genaiVideoGenerationApiDefinitions } from "./apiDefinitions/genai";
import { transcriptApiDefinitions } from "./apiDefinitions/api/transcripts";
import { userPreferenceApiDefinitions } from "./apiDefinitions/api/userPreferences";
import { userManagementApiDefinitions } from "./apiDefinitions/api/userManagement";
import { onboardingApiDefinitions } from "./apiDefinitions/api/onboarding";
import { amazonOnboardingApiDefinitions } from "./apiDefinitions/api/amazonOnboarding";
import { amazonAdvertisingOAuthApiDefinitions } from "./apiDefinitions/api/amazonAdvertisingOAuth";
import { spApiOAuthApiDefinitions } from "./apiDefinitions/api/spApiOAuth";
import { trmnlApiDefinitions } from "./apiDefinitions/api/trmnl";
import { searchTermPerformanceReportApiDefinitions } from "./apiDefinitions/api/v4Query/searchTermPerformance";
import { adsApiDefinitions } from "./apiDefinitions/api/ads";
import { categoryReportApiDefinitions } from "./apiDefinitions/api/v4Query/category";
import { insightsApiDefinitions } from "./apiDefinitions/api/insights";
import { sellerInfoApiDefinitions } from "./apiDefinitions/api/sellerInfo";
import { channelCampaignsApiDefinitions } from "./apiDefinitions/campaign_channel";
import { campaignAdMapApiDefinitions } from "./apiDefinitions/campaign_ad_map";
import { shelfStatsApiDefinitions } from "./apiDefinitions/api/shelfStats";
import { annotationsApiDefinitions } from "./apiDefinitions/api/annotations";
import { walmartApiDefinitions } from "./apiDefinitions/api/walmart";
import { liveDashboardApiDefinitions } from "./apiDefinitions/api/liveDashboard";
import { orgStatusApiDefinitions } from "./apiDefinitions/api/orgStatus";
import { campaignStatsHourlyApiDefinitions } from "./apiDefinitions/api/campaignStatsHourly";
import { agencyApiDefinitions } from "./apiDefinitions/api/agencies";
import { tagsApiDefinitions } from "./apiDefinitions/api/tags";
import { kikReachApiDefinitions } from "./apiDefinitions/api/kikreach";
import { assetsApiDefinitions } from "./apiDefinitions/api/assets";
import { dspOrderDatasetsApiDefinitions } from "./apiDefinitions/api/dsp/orderDatasets";
import { dspGeoDatasetsApiDefinitions } from "./apiDefinitions/api/dsp/geoDatasets";
import { dspAudienceDatasetsApiDefinitions } from "./apiDefinitions/api/dsp/audienceDatasets";
import { dspInventoryDatasetsApiDefinitions } from "./apiDefinitions/api/dsp/inventoryDatasets";
import { dspProductDatasetsApiDefinitions } from "./apiDefinitions/api/dsp/productDatasets";
import { dspLineItemDatasetsApiDefinitions } from "./apiDefinitions/api/dsp/lineItemDatasets";
import { genericDatasetsQueryApiDefinitions } from "./apiDefinitions/api/datasets/genericQuery";
import { dspTargetApiDefinitions } from "./apiDefinitions/api/dsp/target";
import { chromeTokenApiDefinitions } from "./apiDefinitions/api/chrometoken";
import { spVideoApiDefinitions } from "./apiDefinitions/api/spVideo";
import { onboardingSummaryApiDefinitions } from "./apiDefinitions/api/onboardingSummary";
import { sbOptimizationRecommendationsApiDefinitions } from "./apiDefinitions/api/sbOptimizationRecommendations";
import { storesApiDefinitions } from "./apiDefinitions/api/stores";
import { amazonAdsConnectionStatusDefinitions } from "./apiDefinitions/api/amazonAdsConnectionStatus";
import { advertisementHistoryApiDefinitions } from "./apiDefinitions/api/advertisementHistory";
import { sqpReportsApiDefinitions } from "./apiDefinitions/api/sqpReports";
import { mcpOAuthApiDefinitions } from "./apiDefinitions/api/mcpOAuth";
import { bidSimulationApiDefinitions } from "./apiDefinitions/api/bidSimulation";
import { sponsoredAdsAudienceDatasetsApiDefinitions } from "./apiDefinitions/api/sponsoredAdsAudience";
import { dataViewsApiDefinitions } from "./apiDefinitions/api/dataViews";
import { dspClientApiDefinitions } from "./apiDefinitions/api/dspClients";
import { dspProfileApiDefinitions } from "./apiDefinitions/api/dspProfiles";
import { advertisingAccountsApiDefinitions } from "./apiDefinitions/api/advertisingAccounts";

const apiDefinition = makeApi([
  ...gvadsApiDefinitions,
  ...dspClientApiDefinitions,
  ...dspProfileApiDefinitions,
  ...pricingApiDefinitions,
  ...spApiCredentialCheckDefinitions,
  ...workflowsApiDefinitions,
  ...experimentalApiDefinitions,
  ...shopifyApiDefinitions,
  ...appsApiDefinitions,
  ...adReportApiDefinitions,
  ...adsApiDefinitions,
  ...productReportApiDefinitions,
  ...ticketsApiDefinitions,
  ...exportCsvEndpoint,
  ...pricingPlanReportApiDefinitions,
  ...productAdsApiDefinitions,
  ...auditLogApiDefinitions,
  ...bugReportApiDefinitions,
  ...targetReportApiDefinitions,
  ...orgReportApiDefinitions,
  ...harvestersApiDefinitions,
  ...taggersApiDefinitions,
  ...programsApiDefinitions,
  ...portfoliosApiDefinitions,
  ...scheduleSyncActionsApiDefinitions,
  ...queryApiDefinitions,
  ...stageApiDefinitions,
  ...queryStages,
  ...orgBulkActionApiDefinitions,
  ...profilesApiDefinitions,
  ...registrationApiDefinitions,
  ...goalsApiDefinitions,
  ...accountsApiDefinitions,
  ...organizationsApiDefinitions,
  ...agencyApiDefinitions,
  ...userProfileApiDefinitions,
  ...userPreferenceApiDefinitions,
  ...productAdApiDefinitions,
  ...storePageProductsApiDefinitions,
  ...searchQueryPerformanceReportApiDefinitions,
  ...sqpReportsApiDefinitions,
  ...searchTermPerformanceReportApiDefinitions,
  ...genaiVideoGenerationApiDefinitions,
  ...transcriptApiDefinitions,
  ...amcApiDefinitions,
  ...userManagementApiDefinitions,
  ...onboardingApiDefinitions,
  ...amazonOnboardingApiDefinitions,
  ...amazonAdvertisingOAuthApiDefinitions,
  ...spApiOAuthApiDefinitions,
  ...trmnlApiDefinitions,
  ...recommendApiDefinitions,
  ...catalogApiDefinitions,
  ...categoryReportApiDefinitions,
  ...insightsApiDefinitions,
  ...sellerInfoApiDefinitions,
  ...channelCampaignsApiDefinitions,
  ...campaignAdMapApiDefinitions,
  ...shelfStatsApiDefinitions,
  ...annotationsApiDefinitions,
  ...uploadsApiDefinitions,
  ...walmartApiDefinitions,
  ...liveDashboardApiDefinitions,
  ...orgStatusApiDefinitions,
  ...campaignStatsHourlyApiDefinitions,
  ...tagsApiDefinitions,
  ...kikReachApiDefinitions,
  ...assetsApiDefinitions,
  ...dspOrderDatasetsApiDefinitions,
  ...dspGeoDatasetsApiDefinitions,
  ...dspLineItemDatasetsApiDefinitions,
  ...genericDatasetsQueryApiDefinitions,
  ...dspProductDatasetsApiDefinitions,
  ...dspAudienceDatasetsApiDefinitions,
  ...dspInventoryDatasetsApiDefinitions,
  ...sponsoredAdsAudienceDatasetsApiDefinitions,
  ...dspTargetApiDefinitions,
  ...chromeTokenApiDefinitions,
  ...spVideoApiDefinitions,
  ...onboardingSummaryApiDefinitions,
  ...sbOptimizationRecommendationsApiDefinitions,
  ...storesApiDefinitions,
  ...amazonAdsConnectionStatusDefinitions,
  ...advertisementHistoryApiDefinitions,
  ...mcpOAuthApiDefinitions,
  ...bidSimulationApiDefinitions,
  ...dataViewsApiDefinitions,
  ...advertisingAccountsApiDefinitions,
]);

export const apiClient = new Zodios(getURLPrefix(), apiDefinition, {
  axiosInstance: axios,
});
